import dayjs from "dayjs";

import type { TRoom } from "@/features/hotels/types/types";
import type { TemporallyStoreFlightBookingDetailsTraveler } from "@/types/codegen";
import type { Traveller } from "@/types/types";
import { MIN_ADULT_AGE, MIN_CHILD_AGE } from "@/utils/constants";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

export function getGroupedTravellersFromRooms(rooms: TRoom[]) {
  return rooms.reduce(
    (acc, room) => {
      const childrenByAge = room.childrenAges.reduce(
        (acc, age) => {
          const type = getTravelerTypeOfAge(Number(age));
          if (type === "child") {
            return {
              ...acc,
              children: acc.children + 1,
            };
          }

          if (type === "infant") {
            return {
              ...acc,
              infants: acc.infants + 1,
            };
          }

          return acc;
        },
        {
          children: 0,
          infants: 0,
        },
      );

      return {
        adults: acc.adults + room.adults,
        children: acc.children + childrenByAge.children,
        infants: acc.infants + childrenByAge.infants,
      };
    },
    {
      adults: 0,
      children: 0,
      infants: 0,
    },
  );
}

export function travelerTypeToTypeCode(type: "adult" | "child" | "infant") {
  if (type === "adult") {
    return "ADT";
  }
  if (type === "child") {
    return "CHD";
  }
  return "INF";
}

export function getTravelerPassengerTypeCode(
  traveler: Pick<Traveller, "birthDate">,
) {
  const age = getAgeFromDateOfBirth(traveler.birthDate);
  const type = getTravelerTypeOfAge(age);
  return travelerTypeToTypeCode(type);
}

export function getTravelerIntlLabel(code: string) {
  if (code === "ADT") {
    return "travellers.adult";
  }

  if (code === "CHD") {
    return "travellers.child";
  }

  return "travellers.infant";
}

// Accepts a birthDate string and returns the traveler type
export function getTravelerType(birthDate: string) {
  const age = getAgeFromDateOfBirth(birthDate);
  return getTravelerTypeOfAge(age);
}

export function getTravelerTypeOfAge(age: number) {
  if (age >= MIN_ADULT_AGE) {
    return "adult";
  }

  if (age >= MIN_CHILD_AGE) {
    return "child";
  }

  return "infant";
}

export function transformFlightTraveler(
  traveler: Traveller,
): TemporallyStoreFlightBookingDetailsTraveler {
  return {
    ...traveler,
    passportExpiryDate: dayjs(traveler.passportExpiryDate).format("YYYY-MM-DD"),
    passportIssuingDate: dayjs(traveler.passportIssuingDate).format(
      "YYYY-MM-DD",
    ),
    emailAddress: traveler.emailAddress || undefined,
    passengerTypeCode: getTravelerPassengerTypeCode(traveler),
    type: getTravelerType(traveler.birthDate),
    birthDate: dayjs(traveler.birthDate).format("YYYY-MM-DD"),
    phoneNumber: traveler.phoneNumber.replace(traveler.phonePrefix, ""),
  };
}
